import { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import Typography from '@primitives/Typography/Typography';
import Button from '@primitives/Button/Button';
import HistoryIcon from '../../../../assets/icons/HistoryIcon';
import UploadFileIcon from 'src/assets/icons/UploadFileIcon';
import { CheckIcon, CloseIcon, InfoIcon } from '@navi/web-ui/lib/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import ChevronDown from '@cp/src/assets/icons/ChevronDown';
import UploadHistory from './UploadHistory/UploadHistory';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchGuidelines,
  fetchSampleFile,
  fetchUploadHistory,
  fetchUploadHistoryPolling,
  uploadFile
} from '../../AmeyoUtilityActions';
import { RootState } from '@cp/src/store';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import { Chip } from '@navi/web-ui/lib/primitives';
import ChevronUp from '@cp/src/assets/icons/ChevronUp';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import { useNavigate } from 'react-router';
import { toast } from '@primitives/Toast';
import {
  ErrMessages,
  FILE_STATUS,
  Message,
  UploadFileErrorMessage,
  UploadFileResponse,
  PURGEOPTIONS
} from '../../types';
import cx from 'classnames';
import { GenericObject } from '@cp/src/types/CommonConstans';

const AmeyoUploadNumbers = () => {
  const guidelines = useSelector((store: RootState) => store.ameyoUtility?.description);
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const [purgeClicked, setPurgeClicked] = useState<PURGEOPTIONS | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const GUIDELINES_ITEM_SIZE = 20;
  const TOTAL_GUIDELINES = 12;
  const handleUploadHistory = () => {
    setShowUploadHistory(true);
  };

  const handleExpandClick = () => {
    if (!isExpanded) {
      dispatch(fetchGuidelines());
    }
    setIsExpanded(!isExpanded);
  };

  const handleUploadClick = () => {
    hiddenFileInput.current?.focus();
    hiddenFileInput.current?.click();
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    handleFile(selectedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files?.[0];
    handleFile(selectedFile);
  };
  const MAX_FILE_SIZE_IN_MB = 5;
  const MAX_FILE_SIZE = 1e6 * MAX_FILE_SIZE_IN_MB;
  const validationOfFile = (file: File | undefined) => {
    if (file) {
      if (file?.size > MAX_FILE_SIZE) {
        return false;
      }
    }
    return true;
  };
  const handleFile = (file: File | undefined) => {
    if (file) {
      setUploadedFile(file);
      if (!validationOfFile(file)) {
        setHasError(true);
        setTimeout(() => {
          setUploadedFile(null);
          if (hiddenFileInput && hiddenFileInput?.current) {
            hiddenFileInput.current.value = '';
          }
          setHasError(false);
        }, 2000);
      } else {
        setHasError(false);
      }
    }
  };
  const handleCancel = () => {
    setUploadedFile(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    setHasError(false);
  };
  const handlePurgeClick = (chip: PURGEOPTIONS) => {
    if (!chip) {
      return;
    }
    setPurgeClicked(chip);
  };

  const processError = (response: UploadFileResponse | null) => {
    if (!response) {
      return;
    }
    setUploadedFile(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    setPurgeClicked(null);

    if (response?.message === UploadFileErrorMessage.UPLOAD_FILE) {
      setMessage({ success: false, error: true, message: ErrMessages.UPLOAD });
    } else {
      setMessage({ success: false, error: true, message: ErrMessages.DEFAULT_MESSAGE });
    }
  };
  const handleButtonClick = async () => {
    if (uploadedFile) {
      const purge = purgeClicked === 'Yes';
      const response = await uploadFile(uploadedFile, purge);
      if (response?.success) {
        setUploadedFile(null);
        if (hiddenFileInput && hiddenFileInput?.current) {
          hiddenFileInput.current.value = '';
        }
        setPurgeClicked(null);
        setMessage({ success: true, error: false, message: '' });
        setSuccessToast();
      } else {
        processError(response);
      }
      setTimeout(() => {
        setMessage({ error: false, success: false, message: '' });
      }, 5000);
    }
  };
  const handleDownloadClick = () => {
    dispatch(fetchSampleFile());
  };
  const setSuccessToast = async () => {
    let filesCount = 10;
    await fetchUploadHistoryPolling(0, 20, '', '').then(response => {
      const files = response?.data?.data?.filter(
        (file: GenericObject) =>
          file?.status === FILE_STATUS.INPROGRESS || file?.status === FILE_STATUS.INITIATED
      );
      filesCount = files?.length;
    });
    const toastMessage = () => {
      return (
        <div>
          <Typography variant={'p3'}>
            {filesCount > 1
              ? `${filesCount} Files upload is in progress`
              : `File upload is in progress`}
          </Typography>
          <div className={styles.toastStyle}>
            <Button variant="text" className={styles.toastText} onClick={handleUploadHistory}>
              Go To Upload History
            </Button>
            <Typography variant={'p3'}>to check status</Typography>
          </div>
        </div>
      );
    };
    toast(toastMessage, { type: 'info' });
  };
  useEffect(() => {
    if (uploadedFile && hasError) {
      toast('Please upload file under 5 MB', { type: 'error' });
    }
  }, [hasError]);

  return (
    <div>
      <div className={styles.topNavContainer}>
        <div className={cx('cursor-pointer')} onClick={() => navigate(-1)}>
          <BackIcon size={'16'} />
        </div>
      </div>
      <div className={styles.container}>
        {message?.error && (
          <div className={styles.failedBanner}>
            <ErrorIconOutline fillColor1="var(--red-base)" />
            <span className={styles.message}>{message?.message}</span>
            <Button
              variant="text"
              onClick={() => setMessage({ error: false, success: false, message: '' })}
            >
              <CloseIcon />
            </Button>
          </div>
        )}
        <div className={styles.MiddleContainer}>
          <div className={styles.card}>
            <div className={styles.headerContainer}>
              <Typography variant={'h3'}>Select File</Typography>
              <Button
                variant="text"
                size="medium"
                startAdornment={<HistoryIcon />}
                onClick={handleUploadHistory}
              >
                View Upload history
                <UploadHistory
                  showUploadDrawer={showUploadHistory}
                  setShowUploadDrawer={setShowUploadHistory}
                />
              </Button>
            </div>

            <div
              className={cx({
                [styles.subContainer]: true,
                [styles.dragover]: isDragging && !uploadedFile
              })}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              <UploadFileIcon />
              <input
                id="fileUpload"
                type="file"
                accept=".csv"
                onChange={handleChange}
                ref={hiddenFileInput}
                style={{ display: 'none' }}
              />
              <div className={styles.textContainer}>
                <Button variant="text" onClick={handleUploadClick}>
                  Click to Select
                </Button>{' '}
                or drag and drop here
              </div>

              <div className={styles.text1Wrapper}>
                <Typography variant={'p5'} className={styles.text1}>
                  Upload only .csv
                </Typography>
                <div className={styles.seperator} />
                <Typography variant={'p5'} className={styles.text1}>
                  Max 5 MB size allowed
                </Typography>
              </div>
              <Button onClick={handleDownloadClick} variant="text" className={styles.text2}>
                {' '}
                Download Sample file
              </Button>
            </div>

            {uploadedFile && !hasError ? (
              <div className={styles.uploadBanner}>
                <CheckIcon color="var(--green-base)" fill="var(--green-base)" />
                <span className={styles.fileName}>{uploadedFile?.name}</span>
                <Button variant="text" onClick={handleCancel}>
                  <CloseIcon />
                </Button>
              </div>
            ) : null}
            <div>
              <Typography variant={'p5'} className={styles.text3}>
                Do you want to replace the previously uploaded numbers?
                <Tooltip placement="top">
                  <TooltipTrigger>
                    <InfoIcon className={styles.infoIcon} />
                  </TooltipTrigger>
                  <TooltipContent className={styles.tooltipContent}>
                    Choosing Yes replaces the previous entries; Choosing No adds the numbers to the
                    existing list.
                  </TooltipContent>
                </Tooltip>
              </Typography>
            </div>
            <div className={styles.buttonContainer}>
              <Chip
                label={PURGEOPTIONS.YES}
                onChipClick={() => handlePurgeClick(PURGEOPTIONS.YES)}
                labelClasses={purgeClicked ? styles.selected : ''}
                selected={purgeClicked === PURGEOPTIONS.YES}
              />
              <Chip
                label={PURGEOPTIONS.NO}
                onChipClick={() => handlePurgeClick(PURGEOPTIONS.NO)}
                labelClasses={purgeClicked ? styles.selected : ''}
                selected={purgeClicked === PURGEOPTIONS.NO}
              />
            </div>
            <div className={styles.BigButton}>
              <Button
                disabled={!purgeClicked || !uploadedFile || hasError}
                onClick={handleButtonClick}
              >
                Upload to Ameyo
              </Button>
            </div>
          </div>
          <div className={styles.collapseHeader}>
            <Button
              variant="text"
              size="medium"
              endAdornment={isExpanded ? <ChevronUp /> : <ChevronDown />}
              onClick={handleExpandClick}
            >
              Read guidelines for file checks
            </Button>
          </div>
          <div
            className={styles.collapsibleContainer}
            style={
              isExpanded
                ? { height: GUIDELINES_ITEM_SIZE * TOTAL_GUIDELINES, paddingBottom: 11 }
                : { height: 0 }
            }
          >
            {guidelines?.map((guideline, index) => {
              return (
                <Typography variant={'p5'} key={index} className={styles.guideline}>
                  {index + 1}. {guideline}
                </Typography>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AmeyoUploadNumbers;
